import type { RemoteVersion } from "./update-state";

/**
 * Reads the build marker that the `emit-version-marker` Vite plugin writes
 * to `dist/version.json`: `{ buildId, generation, assets, commit, builtAt }`.
 * `buildId` is the basename of the content-hashed entry chunk, so any code or
 * CSS change yields a new id.
 */
const VERSION_URL = "/version.json";

/**
 * Build id of the bundle running in this tab, taken from the entry
 * `<script src="/assets/...">` tag. Null in Vite dev mode (the dev server
 * serves `/src/main.tsx`), which turns the whole update flow into a no-op.
 */
export function captureBaselineBuildId(): string | null {
  const src = document
    .querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')
    ?.getAttribute("src");
  return src ? (src.split("/").pop() ?? null) : null;
}

/**
 * Fetches the deployed marker. Returns null on any network, HTTP, shape, or
 * abort failure (never rejects): a blip during a deploy must never surface
 * to the user.
 */
export async function fetchRemoteVersion(
  signal: AbortSignal,
): Promise<RemoteVersion | null> {
  let res: Response;
  try {
    res = await fetch(VERSION_URL, { cache: "no-store", signal });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  let data: unknown;
  try {
    data = await res.json();
  } catch {
    return null;
  }
  if (typeof data !== "object" || data === null || !("buildId" in data)) {
    return null;
  }
  const { buildId } = data;
  if (typeof buildId !== "string") return null;
  const generation = "generation" in data ? data.generation : null;
  return {
    buildId,
    generation: Number.isInteger(generation) ? (generation as number) : null,
  };
}
